import { Control, useFieldArray } from 'react-hook-form';
import { Grid, Typography } from '../../../../base';
import {
    IApplyQuestion,
    IApplyNowAdditionalQuestionsForm,
    ApplyNowFormValues,
    QuestionAnswerFields,
    QuestionType,
    TQuestionAnswer,
    QuestionMode,
} from '../../../../common';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { QuestionsList } from '../../../SaveJob/SaveJobSteps/JobQuestions/components';

interface AdditionalQuestionsStepProps {
    applyQuestions: Array<IApplyQuestion>;
    control: Control<IApplyNowAdditionalQuestionsForm>;
}

const getDefaultAnswerForType = (type: QuestionType): TQuestionAnswer => {
    switch (type) {
        case QuestionType.SINGLE_SELECT:
            return '';
        case QuestionType.MULTIPLE_SELECT:
            return [];
        case QuestionType.DATE_RANGE:
            return { from: null, to: null };
        case QuestionType.FILE_UPLOAD:
            return null;
        default:
            return '';
    }
};

const getQuestionsFingerprint = (questions: Array<IApplyQuestion>) =>
    questions
        .map((question) =>
            [
                question.id,
                question.question,
                question.type,
                question.isRequired,
                (question.options || [])
                    .map((option) => `${option.value}:${option.label}`)
                    .join('|'),
            ].join('~')
        )
        .join('||');

const AdditionalQuestionsStep = (props: AdditionalQuestionsStepProps) => {
    const { applyQuestions, control } = props;
    const { t } = useTranslation();
    const lastFingerprint = useRef<string>('');

    const { replace } = useFieldArray({
        control,
        name: ApplyNowFormValues.questionAnswers,
    });

    useEffect(() => {
        const fingerprint = getQuestionsFingerprint(applyQuestions);
        if (fingerprint === lastFingerprint.current) return;
        lastFingerprint.current = fingerprint;

        replace(
            applyQuestions.map((q) => ({
                [QuestionAnswerFields.QUESTION_ID]: q.id,
                [QuestionAnswerFields.ANSWER]: getDefaultAnswerForType(q.type),
            }))
        );
    }, [applyQuestions, replace]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography variant="subtitle1">
                    {t('applyNow.additionalQuestions')}
                </Typography>
            </Grid>
            {applyQuestions.length > 0 ? (
                <QuestionsList
                    control={
                        control as Control<IApplyNowAdditionalQuestionsForm>
                    }
                    mode={QuestionMode.SUBMIT}
                    applyQuestions={applyQuestions}
                />
            ) : (
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                        {t('applyNow.noAdditionalQuestions')}
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
};

export default AdditionalQuestionsStep;
